// 4. Type imports
import type { WizardUseCase } from '~/types/onboarding';

// 5. Custom hooks and utilities
import { getResolvedApiBaseUrl } from '~/config';

/**
 * Connection type for API key generation.
 */
export type ConnectionType = 'rest' | 'mcp';

/**
 * Get suggested topic name based on user's selected use case.
 *
 * Returns descriptive topic name that aligns with use case context.
 * Used to pre-fill topic creation input during onboarding.
 *
 * @param useCase - Selected wizard use case or null if not selected
 * @returns Suggested topic name string
 *
 * @example
 * ```typescript
 * getTopicNameSuggestion('agent_communications') // "Agent Messages"
 * getTopicNameSuggestion(null) // "My First Topic"
 * ```
 */
export function getTopicNameSuggestion(useCase: WizardUseCase | null): string {
  if (!useCase) {
    return 'My First Topic';
  }

  const suggestions: Record<WizardUseCase, string> = {
    agent_communications: 'Agent Messages',
    team_notifications: 'Team Alerts',
    customer_alerts: 'Customer Notifications',
    iot_data_collection: 'IoT Data Stream',
  };

  return suggestions[useCase];
}

/**
 * Get recommended connection type based on user's selected use case.
 *
 * Returns connection type that best fits the use case workflow.
 * Used to guide API key creation during onboarding.
 *
 * @param useCase - Selected wizard use case or null if not selected
 * @returns Recommended connection type ('rest' or 'mcp')
 *
 * @example
 * ```typescript
 * getConnectionRecommendation('agent_communications') // "mcp"
 * getConnectionRecommendation('customer_alerts') // "rest"
 * getConnectionRecommendation(null) // "rest"
 * ```
 */
export function getConnectionRecommendation(useCase: WizardUseCase | null): ConnectionType {
  if (!useCase) {
    return 'rest';
  }

  const recommendations: Record<WizardUseCase, ConnectionType> = {
    agent_communications: 'mcp',
    team_notifications: 'rest',
    customer_alerts: 'rest',
    iot_data_collection: 'rest',
  };

  return recommendations[useCase];
}

/**
 * Generate curl command for sending test message to topic.
 *
 * Returns complete curl command string with API key and topic name.
 * Used to display copy-paste example during onboarding.
 *
 * @param apiKey - Generated API key (format: ddrp_rest_... or ddrp_mcp_...)
 * @param topicName - Name of created topic
 * @returns Formatted curl command string with line breaks
 *
 * @example
 * ```typescript
 * const cmd = generateCurlCommand('ddrp_rest_abc123', 'Agent Messages');
 * // Returns:
 * // curl -X POST https://api.ddrp.sh/v1/messages \
 * //   -H "Authorization: Bearer ddrp_rest_abc123" \
 * //   -H "Content-Type: application/json" \
 * //   -d '{"topic": "Agent Messages", "content": "Hello from DeadDrop!"}'
 * ```
 */
export function generateCurlCommand(apiKey: string, topicName: string): string {
  const apiBaseUrl = getResolvedApiBaseUrl();

  return `curl -X POST ${apiBaseUrl}/v1/messages \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"topic": "${topicName}", "content": "Hello from DeadDrop!"}'`;
}

/**
 * Generate retrieval instructions for reading messages from topic.
 *
 * Returns connection-specific instructions for retrieving messages.
 * REST instructions show curl GET command with API key authentication.
 * MCP instructions show tool usage example for read_messages tool.
 *
 * @param connectionType - Connection type selected by user ('rest' or 'mcp')
 * @param apiKey - Generated API key with credentials
 * @param topicId - UUID of created topic
 * @returns Formatted retrieval instructions string
 *
 * @example
 * ```typescript
 * const instructions = generateRetrievalInstructions(
 *   'rest',
 *   'ddrp_rest_abc123',
 *   '550e8400-e29b-41d4-a716-446655440000'
 * );
 * // Returns: curl -H "X-API-Key: ddrp_rest_abc123" \
 * //   http://localhost:10010/v1/topics/550e8400-e29b-41d4-a716-446655440000/messages
 * ```
 */
export function generateRetrievalInstructions(
  connectionType: 'rest' | 'mcp',
  apiKey: string,
  topicId: string
): string {
  const apiBaseUrl = getResolvedApiBaseUrl();

  if (connectionType === 'mcp') {
    return `Use the read_messages tool to retrieve messages from your topic.`;
  }

  // REST: curl GET command with API key authentication
  return `curl -H "X-API-Key: ${apiKey}" \\
  ${apiBaseUrl}/v1/topics/${topicId}/messages`;
}

/**
 * Onboarding code template with syntax highlighting language.
 *
 * Extended template structure for onboarding wizard with language field
 * for syntax highlighting in code blocks.
 */
export interface OnboardingTemplate {
  id: string;
  label: string;
  language: string;
  code: string;
}

/**
 * Generate connection-specific code templates for onboarding wizard.
 *
 * Returns array of code templates with real credentials injected.
 * Templates demonstrate how to send messages to the created topic.
 *
 * MCP templates show configuration for MCP clients.
 * REST templates show message sending via REST API.
 *
 * @param connectionType - Connection type selected by user ('rest' or 'mcp')
 * @param apiKey - Generated API key with credentials
 * @param topicId - UUID of created topic
 * @param topicName - Display name of created topic
 * @returns Array of OnboardingTemplate objects for TemplateCodeBlock
 *
 * @example
 * ```typescript
 * const templates = generateOnboardingCode(
 *   'rest',
 *   'ddrp_rest_abc123',
 *   '550e8400-e29b-41d4-a716-446655440000',
 *   'Agent Messages'
 * );
 * // Returns: [
 * //   { id: 'rest-curl', label: 'cURL', language: 'bash', code: '...' },
 * //   { id: 'rest-axios', label: 'JavaScript/TypeScript - Axios', language: 'typescript', code: '...' },
 * //   ...
 * // ]
 * ```
 */
export function generateOnboardingCode(
  connectionType: 'rest' | 'mcp',
  apiKey: string,
  _topicId: string,
  topicName: string
): OnboardingTemplate[] {
  const apiBaseUrl = getResolvedApiBaseUrl();

  if (connectionType === 'mcp') {
    return [
      {
        id: 'mcp-json',
        label: 'Generic MCP configuration',
        language: 'json',
        code: JSON.stringify(
          {
            mcpServers: {
              deaddrop: {
                type: 'http',
                url: `${apiBaseUrl}/mcp`,
                headers: {
                  'X-API-Key': apiKey,
                },
              },
            },
          },
          null,
          2
        ),
      },
      {
        id: 'mcp-claude-linux',
        label: 'Claude Code (Linux/Mac)',
        language: 'bash',
        code:
          `claude mcp add deaddrop ${apiBaseUrl}/mcp \\\n` +
          `  --scope project \\\n` +
          `  --transport http \\\n` +
          `  --header "X-API-Key: ${apiKey}"`,
      },
      {
        id: 'mcp-claude-powershell',
        label: 'Claude Code (Windows/PowerShell)',
        language: 'powershell',
        code:
          `claude mcp add deaddrop ${apiBaseUrl}/mcp \`\n` +
          `  --scope project \`\n` +
          `  --transport http \`\n` +
          `  --header "X-API-Key: ${apiKey}"`,
      },
    ];
  }

  // REST templates
  return [
    {
      id: 'rest-curl',
      label: 'cURL',
      language: 'bash',
      code:
        `curl -X POST \\\n` +
        `  -H "Content-Type: application/json" \\\n` +
        `  -H "X-API-Key: ${apiKey}" \\\n` +
        `  -d '{"topic": "${topicName}", "content": "Hello from DeadDrop!"}' \\\n` +
        `  ${apiBaseUrl}/v1/messages`,
    },
    {
      id: 'rest-axios',
      label: 'JavaScript/TypeScript - Axios',
      language: 'typescript',
      code:
        `import axios from 'axios';\n\n` +
        `const response = await axios.post(\n` +
        `  '${apiBaseUrl}/v1/messages',\n` +
        `  { topic: '${topicName}', content: 'Hello from DeadDrop!' },\n` +
        `  {\n` +
        `    headers: {\n` +
        `      'Content-Type': 'application/json',\n` +
        `      'X-API-Key': '${apiKey}'\n` +
        `    }\n` +
        `  }\n` +
        `);`,
    },
    {
      id: 'rest-fetch',
      label: 'JavaScript/TypeScript - Fetch',
      language: 'typescript',
      code:
        `const response = await fetch('${apiBaseUrl}/v1/messages', {\n` +
        `  method: 'POST',\n` +
        `  headers: {\n` +
        `    'Content-Type': 'application/json',\n` +
        `    'X-API-Key': '${apiKey}'\n` +
        `  },\n` +
        `  body: JSON.stringify({ topic: '${topicName}', content: 'Hello from DeadDrop!' })\n` +
        `});\n\n` +
        `const data = await response.json();`,
    },
    {
      id: 'rest-python',
      label: 'Python Requests',
      language: 'python',
      code:
        `import requests\n\n` +
        `response = requests.post(\n` +
        `    '${apiBaseUrl}/v1/messages',\n` +
        `    headers={\n` +
        `        'Content-Type': 'application/json',\n` +
        `        'X-API-Key': '${apiKey}'\n` +
        `    },\n` +
        `    json={'topic': '${topicName}', 'content': 'Hello from DeadDrop!'}\n` +
        `)`,
    },
    {
      id: 'rest-powershell',
      label: 'PowerShell',
      language: 'powershell',
      code:
        `$headers = @{\n` +
        `    "Content-Type" = "application/json"\n` +
        `    "X-API-Key" = "${apiKey}"\n` +
        `}\n\n` +
        `$body = @{\n` +
        `    topic = "${topicName}"\n` +
        `    content = "Hello from DeadDrop!"\n` +
        `} | ConvertTo-Json\n\n` +
        `Invoke-RestMethod -Uri "${apiBaseUrl}/v1/messages" \`\n` +
        `    -Method Post \`\n` +
        `    -Headers $headers \`\n` +
        `    -Body $body`,
    },
  ];
}
